import { Lock, ShieldCheck, History, Loader2, Plus } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB") : "");

export const FormSignOffPanel = ({
  r,
  signName, setSignName, signing, onSign,
  amendOpen, setAmendOpen, amend, setAmend, amending, onAmend,
}) => {
  const locked = r?.status === "locked";
  const amendments = r?.amendments || [];

  return (
    <div className="mt-6 pt-5 border-t" style={{ borderColor: "var(--fl-border)" }} data-testid="form-signoff-panel">
      {!locked ? (
        <div className="fl-card p-4" data-testid="form-signoff-unlocked">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck size={15} className="text-[#f47a20]" />
            <div className="fl-label">Clinical sign-off</div>
          </div>
          <p className="text-xs mb-3" style={{ color: "var(--fl-muted-2)" }}>
            Confirming locks an immutable, signed snapshot of this response. After locking,
            changes can only be made as append-only amendments.
          </p>
          <label className="fl-label mb-1 block">Typed signature (your name)</label>
          <input
            className="fl-input mb-3"
            placeholder="e.g. Dr Jane Smith"
            value={signName}
            onChange={(e) => setSignName(e.target.value)}
            data-testid="signoff-name-input"
          />
          <button
            className="fl-btn justify-center"
            disabled={signing || !signName.trim()}
            onClick={onSign}
            data-testid="signoff-confirm-lock"
          >
            {signing ? <Loader2 className="animate-spin" size={14} /> : <Lock size={14} />}
            {signing ? "Locking…" : "Confirm & Lock"}
          </button>
        </div>
      ) : (
        <div className="fl-card p-4" data-testid="form-signoff-locked">
          <div className="flex items-center gap-2 mb-2">
            <Lock size={15} className="text-green-500" />
            <div className="fl-label text-green-500">Signed &amp; locked</div>
          </div>
          <div className="text-xs" style={{ color: "var(--fl-muted-2)" }} data-testid="signoff-meta">
            Signed by <b>{r.signed_name || r.signed_by_name || "Clinician"}</b>
            {r.locked_at ? ` · ${fmt(r.locked_at)}` : ""}
          </div>

          {amendments.length > 0 && (
            <div className="mt-4" data-testid="amendments-list">
              <div className="flex items-center gap-2 mb-2">
                <History size={13} className="text-[#f47a20]" />
                <div className="fl-label">Amendments ({amendments.length})</div>
              </div>
              <div className="space-y-2">
                {amendments.map((a, i) => (
                  <div key={a.id || i} className="fl-card p-3" data-testid={`amendment-${i}`}>
                    <div className="text-sm" style={{ color: "var(--fl-text)" }}>{a.reason}</div>
                    {a.note ? (
                      <div className="text-xs mt-1 whitespace-pre-wrap" style={{ color: "var(--fl-muted-2)" }}>{a.note}</div>
                    ) : null}
                    <div className="text-[11px] mt-1 text-zinc-500">
                      {a.signed_name || a.clinician_name || "Clinician"}{a.at ? ` · ${fmt(a.at)}` : ""}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4">
            {!amendOpen ? (
              <button
                className="fl-btn-ghost text-xs"
                onClick={() => setAmendOpen(true)}
                data-testid="amendment-open"
              >
                <Plus size={12} /> Add amendment
              </button>
            ) : (
              <div className="fl-card p-3" data-testid="amendment-form">
                <label className="fl-label mb-1 block">Reason (required)</label>
                <input
                  className="fl-input mb-2 text-sm"
                  placeholder="Reason for amendment"
                  value={amend.reason}
                  onChange={(e) => setAmend({ ...amend, reason: e.target.value })}
                  data-testid="amendment-reason"
                />
                <label className="fl-label mb-1 block">Note (optional)</label>
                <textarea
                  className="fl-input mb-2 text-sm"
                  rows={2}
                  placeholder="Additional detail"
                  value={amend.note}
                  onChange={(e) => setAmend({ ...amend, note: e.target.value })}
                  data-testid="amendment-note"
                />
                <label className="fl-label mb-1 block">Typed signature (your name)</label>
                <input
                  className="fl-input mb-3 text-sm"
                  placeholder="e.g. Dr Jane Smith"
                  value={amend.name}
                  onChange={(e) => setAmend({ ...amend, name: e.target.value })}
                  data-testid="amendment-name"
                />
                <div className="flex items-center gap-2">
                  <button
                    className="fl-btn text-xs justify-center"
                    disabled={amending || !amend.reason.trim() || !amend.name.trim()}
                    onClick={onAmend}
                    data-testid="amendment-submit"
                  >
                    {amending ? <Loader2 className="animate-spin" size={12} /> : <Plus size={12} />}
                    {amending ? "Saving…" : "Record amendment"}
                  </button>
                  <button
                    className="fl-btn-ghost text-xs"
                    onClick={() => setAmendOpen(false)}
                    disabled={amending}
                    data-testid="amendment-cancel"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FormSignOffPanel;
